import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import { SelectOption } from '../../../types/ViewModel/SelectOption';
import { EventTypeInput } from '../../../types/ViewModel/EventTypeInput';
import { DropdownInputWrapper } from './DropdownInput.styles';
import EventTypeInputFactory from '../../../factory/EventTypeInputFactory';
import { DropdownTypeEnum } from '../../../enums/dropdownTypeEnum';
import { DropdownItem } from '../../../types/ViewModel/DropdownItem';
import { EventType } from '../../../types/ViewModel/EventType';
import { RotationTreeEnum } from '../../../enums/rotationTreeEnum';
import { PortCall } from '../../../types/ViewModel/PortCall';
import { useRotationStore } from '../../../context/RotationContext';
import useRotation from '../../../app/useRotation';
import { DropdownItemFactory } from '../../../factory/DropdownItemFactory';
import { BerthCall } from '../../../types/ViewModel/BerthCall';
import eventInputUtils from '../../../utils/event/eventInputUtils';
import { useVesselStore } from '../../../context/VesselContext';
import { InputWrapper, InputHeadingWrapper, InputFieldWrapper } from '../EventInput.styles';
import { MultiSelect } from 'react-multi-select-component';
import { EventTypeData } from '../../../types/ViewModel/EventTypeData';
import { useEventStore } from '../../../context/EventContext';
import { TankersEvent } from '../../../types/ViewModel/TankersEvent';
import { DropdownGroup } from '../../../types/ViewModel/DropdownGroup';
import { DropdownGroupReg } from '../../../types/ViewModel/DropdownGroupReg';
import useEvent from '../../../app/useEvent';
import { DropdownItemReg } from '../../../types/ViewModel/DropdownItemReg';
import eventtypecommon from '../../../utils/event/eventtypecommon';
import { ErrorMsg } from '../../../types/ViewModel/ErrorMsg';
import eventTypeGroupChangeLogUtils from '../../../utils/event/eventTypeGroupChangeLogUtils';
import { compareActualEventDateTime } from '../../../utils/event/eventDateUtils';
import { useBerthStore } from '../../../context/BerthContext';
import useAppFeature from 'src/app/useAppFeature';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { resolveHeading } from '../../../utils/event/headingOverrides';

type Props = {
    onChange: (selItems: SelectOption[], key: number, isEmpty: boolean, errorMsg: ErrorMsg) => void;
    evType: EventType;
    evTypeInput: EventTypeInput;
    evTypeData: EventTypeData;
    fullEventArray: TankersEvent[];
    key: string;
};

const DropdownInputComponent: React.FC<Props> = (props) => {
    const dropdownItemFactory = new DropdownItemFactory();
    const [eventTypeInput, setEventTypeInput] = useState<EventTypeInput | null>(null);
    const { selectedNode } = useRotationStore();
    const { vessel } = useVesselStore();
    const { getRequiredContextObj, filterCargoOperation } = useRotation();
    const { isDropdownInputValid } = eventInputUtils();
    const { createEventTypeInput } = EventTypeInputFactory();
    const { fullEventArray } = useEventStore();
    const { allBerths } = useBerthStore();
    const { filterOnContextIncludeParent, filterOnContext, isCargoDropdown, isBerthDropdown } = useEvent();
    const { sortOrderedEvents } = eventtypecommon();
    const { getRequiredPreviousTypeId } = eventTypeGroupChangeLogUtils();
    const { isFeatureEnabled } = useAppFeature();

    useEffect(() => {
        const evTypeInp = createEventTypeInput(props.evTypeInput);
        if (evTypeInp.dropdownGroup) {
            if (isCargoDropdown(evTypeInp.dropdownGroup.dropdownTypeId)) {
                evTypeInp.dropdownGroup.dropdownItemArray = getCargoInputs(props.evType, evTypeInp.dropdownGroup.dropdownTypeId, false);
            } else if (isTankListDropdown(evTypeInp.dropdownGroup.dropdownTypeId)) {
                evTypeInp.dropdownGroup.dropdownItemArray = getTankListInputs();
            } else if (isBerthDropdown(evTypeInp.dropdownGroup.dropdownTypeId)) {
                evTypeInp.dropdownGroup.dropdownItemArray = getBerthInputs();
            }
            setEventTypeInput(evTypeInp);
        }
    }, [props.evTypeInput]);

    const sameDropdownGroup = (ev: TankersEvent, dropdownGroup: DropdownGroup | null): DropdownGroupReg | null => {
        if (dropdownGroup && ev.eventTypeInputRegList && ev.eventTypeInputRegList.length > 0) {
            const inpRegItem = ev.eventTypeInputRegList.find((etir) => {
                if (etir.dropdownGroupReg && etir.dropdownGroupReg.eventInputDropdownGroupId === dropdownGroup.eventInputDropdownGroupId) return true;
                return false;
            });
            if (inpRegItem && inpRegItem.dropdownGroupReg) return inpRegItem.dropdownGroupReg;
        }
        return null;
    };

    const findFirstPredecessorWithSameDropdown = (filteredEvList: TankersEvent[]): DropdownGroupReg | null => {
        let sameDropdownGr: DropdownGroupReg | null = null;
        const sameSeqEvList = filteredEvList
            .filter((ev) => {
                if (props.evType.parentSequenceNumber)
                    return (
                        ev.concurrentSequenceNumber === props.evType.parentSequenceNumber &&
                        ev.eventType?.eventTypeGroupId === props.evType.eventTypeGroupId
                    );
                return ev.eventType?.eventTypeGroupId === props.evType.eventTypeGroupId;
            })
            .sort((a, b) => compareActualEventDateTime(a, b, false));

        if (sameSeqEvList && sameSeqEvList.length > 0) {
            const currentEvInd = sameSeqEvList.findIndex((ev) => ev.eventTypeId === props.evType.eventTypeId);
            if (currentEvInd === -1 || currentEvInd > 0) {
                let ind = 0;
                let keepGoing = true;
                while (keepGoing) {
                    const sameDdGrFirst = sameDropdownGroup(sameSeqEvList[ind], props.evTypeInput.dropdownGroup);
                    if (sameDdGrFirst) {
                        sameDropdownGr = sameDdGrFirst;
                        keepGoing = false;
                    } else {
                        ind++;
                        if (currentEvInd === -1) keepGoing = ind < sameSeqEvList.length;
                        else keepGoing = ind < currentEvInd;
                    }
                }
            }
        }
        return sameDropdownGr;
    };

    const findFirstParentEventWithSameDropdown = (filteredEvList: TankersEvent[]): DropdownGroupReg | null => {
        let sameDropdownGr: DropdownGroupReg | null = null;
        if (
            props.evType.parentSequenceNumber &&
            props.evType.eventTypeGroup &&
            props.evTypeInput.dropdownGroup &&
            props.evTypeInput.dropdownGroup.dropdownItemArray &&
            props.evTypeInput.dropdownGroup.dropdownItemArray.length > 0
        ) {
            const reqPrev = getRequiredPreviousTypeId(props.evType.eventTypeGroup, selectedNode?.firstTimeInPortCall);
            const firstParentEv = filteredEvList.find(
                (ev) => ev.concurrentSequenceNumber === props.evType.parentSequenceNumber && ev.eventTypeId === reqPrev
            );
            if (firstParentEv) {
                const sameDdGrFirst = sameDropdownGroup(firstParentEv, props.evTypeInput.dropdownGroup);
                if (sameDdGrFirst) {
                    sameDropdownGr = sameDdGrFirst;
                } else {
                    let keepGoing = true;
                    let preReqEv = firstParentEv;
                    let reqPrevTypeId = getRequiredPreviousTypeId(preReqEv.eventType?.eventTypeGroup, selectedNode?.firstTimeInPortCall);
                    while (keepGoing) {
                        if (preReqEv.concurrentSequenceNumber && reqPrevTypeId) {
                            const parentEv = filteredEvList.find(
                                (ev) => ev.concurrentSequenceNumber === preReqEv.concurrentSequenceNumber && ev.eventTypeId === reqPrevTypeId
                            );
                            if (parentEv) {
                                reqPrevTypeId = getRequiredPreviousTypeId(parentEv.eventType?.eventTypeGroup, selectedNode?.firstTimeInPortCall);
                                if (reqPrevTypeId) {
                                    const sameDdGr = sameDropdownGroup(parentEv, props.evTypeInput.dropdownGroup);
                                    if (sameDdGr) {
                                        keepGoing = false;
                                        sameDropdownGr = sameDdGr;
                                    } else {
                                        preReqEv = parentEv;
                                    }
                                } else {
                                    keepGoing = false;
                                }
                            } else {
                                keepGoing = false;
                            }
                        } else {
                            keepGoing = false;
                        }
                    }
                }
            }
        }
        return sameDropdownGr;
    };

    const findParentEventWithSameDropdown = (): DropdownGroupReg | null => {
        const filteredEvList = filterOnContextIncludeParent(selectedNode, fullEventArray);
        const firstPredecessor = findFirstPredecessorWithSameDropdown(filteredEvList);
        if (firstPredecessor) return firstPredecessor;
        return findFirstParentEventWithSameDropdown(filteredEvList);
    };

    const hasDelayReasonDropdown = (): boolean => {
        if (eventTypeInput && props.evType.eventTypeInputDelayEventTypeMapArray && props.evType.eventTypeInputDelayEventTypeMapArray.length > 0) {
            const evTypeInp = props.evType.eventTypeInputDelayEventTypeMapArray.find((o) => o.eventTypeInputId === eventTypeInput.eventTypeInputId);
            return Boolean(evTypeInp);
        }
        return false;
    };

    const mappedOptions = (): SelectOption[] => {
        let parentDdGr: DropdownGroupReg | null = null;
        //if (!props.evTypeInput.eventTypeInputReg || !props.evTypeInput.eventTypeInputReg.dropdownGroupReg)
        // Check for delay dropdown
        if (!hasDelayReasonDropdown()) {
            parentDdGr = findParentEventWithSameDropdown();
        }

        const itemList: SelectOption[] = eventTypeInput?.dropdownGroup?.dropdownItemArray
            .filter((o) => includeItem(o, parentDdGr))
            .map((item) => {
                return new SelectOption(item.dropdownItemId.toString(), item.name);
            });

        // TODO: VALIDATION - Remove this when feature is enabled for all vessels
        if (!isFeatureEnabled({ featureEnum: AppFeatureEnum.Bunkering, imoNumber: vessel?.imoNumber, date: new Date() })) {
            const hfoItem = itemList.find((item) => item.label === 'HFO (For vessels with EGCS/Scrubbers)');
            if (hfoItem) {
                hfoItem.label = 'HFO';
            }
            const mgoItem = itemList.find((item) => item.label === 'LGO');
            if (mgoItem) {
                mgoItem.label = 'MGO';
            }
            return itemList.filter((item) => item.label !== 'LSF' && item.label !== 'BIO');
        }

        return itemList;
        // return itemList.sort((a, b) => a.label.localeCompare(b.label));
    };

    const includeItem = (drItem: DropdownItem, parentDdGr: DropdownGroupReg | null): boolean => {
        if (drItem.deleted) {
            const selItems = getSelectedItems();
            const item = selItems.find((o) => o.value === drItem.dropdownItemId.toString());
            if (!item) return false;
        }
        if (!parentDdGr) return true;
        const existInParent = parentDdGr.dropdownItemRegList.find((dir) => dir.dropdownItemId === drItem.dropdownItemId);
        if (existInParent) return true;
        return false;
    };

    const findNotSelectedDropdownItem = (selItems: SelectOption[], ev: TankersEvent): DropdownItemReg | null => {
        let dropdownItem: DropdownItemReg | null = null;
        if (ev.eventTypeInputRegList && ev.eventTypeInputRegList.length > 0) {
            let ind = 0;
            let keepGoing = true;
            while (keepGoing) {
                if (
                    ev.eventTypeInputRegList[ind].dropdownGroupReg &&
                    ev.eventTypeInputRegList[ind].dropdownGroupReg?.dropdownItemRegList &&
                    ev.eventTypeInputRegList[ind].dropdownGroupReg?.dropdownItemRegList.length > 0 &&
                    ev.eventTypeInputRegList[ind].dropdownGroupReg?.eventInputDropdownGroupId ===
                        props.evTypeInput.dropdownGroup?.eventInputDropdownGroupId
                ) {
                    const ddItem = ev.eventTypeInputRegList[ind].dropdownGroupReg?.dropdownItemRegList.find((dir) => {
                        const selItem = selItems.find((item) => item.value === dir.dropdownItemId.toString());
                        if (!selItem) return true;
                        return false;
                    });
                    if (ddItem) {
                        keepGoing = false;
                        dropdownItem = ddItem;
                    } else {
                        ind++;
                        keepGoing = ind < ev.eventTypeInputRegList.length;
                    }
                } else {
                    ind++;
                    keepGoing = ind < ev.eventTypeInputRegList.length;
                }
            }
        }
        return dropdownItem;
    };

    const findFirstPredessorInvalidDropdown = (selItems: SelectOption[]): DropdownItemReg | null => {
        let dropdownItem: DropdownItemReg | null = null;
        if (props.evTypeInput.eventTypeInputReg && props.evTypeInput.eventTypeInputReg.dropdownGroupReg && props.evType.parentSequenceNumber) {
            const filteredEvList = filterOnContext(selectedNode, fullEventArray);
            const currentSeq = filteredEvList
                .filter((ev) => {
                    return (
                        ev.concurrentSequenceNumber === props.evType.parentSequenceNumber &&
                        ev.eventType?.eventTypeGroupId === props.evType.eventTypeGroupId
                    );
                })
                .sort((a, b) => compareActualEventDateTime(a, b, false));
            // Find selected event
            const currentEvInd = currentSeq.findIndex((ev) => ev.eventTypeId === props.evType.eventTypeId);
            if (currentEvInd > -1 && currentSeq.length > currentEvInd + 1) {
                let ind = currentEvInd + 1;
                let keepGoing = true;
                while (keepGoing) {
                    const notSelectedDd = findNotSelectedDropdownItem(selItems, currentSeq[ind]);
                    if (notSelectedDd) {
                        dropdownItem = notSelectedDd;
                        keepGoing = false;
                    } else {
                        ind++;
                        keepGoing = ind < currentSeq.length;
                    }
                }
            }
        }
        return dropdownItem;
    };

    const findFirstParentInvalidDropdown = (selItems: SelectOption[]): DropdownItemReg | null => {
        let dropdownItem: DropdownItemReg | null = null;
        if (props.evTypeInput.eventTypeInputReg && props.evTypeInput.eventTypeInputReg.dropdownGroupReg && props.evType.parentSequenceNumber) {
            const filteredEvList = filterOnContext(selectedNode, fullEventArray);

            const childrenSeq = filteredEvList.filter((ev) => {
                return (
                    ev.concurrentSequenceNumber === props.evType.parentSequenceNumber &&
                    getRequiredPreviousTypeId(ev.eventType?.eventTypeGroup, selectedNode?.firstTimeInPortCall) === props.evType.eventTypeId
                );
            });

            if (childrenSeq && childrenSeq.length > 0) {
                let seqIdArray: string[] = childrenSeq.flatMap((o) => o.eventTypeGroupSequenceIdentity);
                seqIdArray = seqIdArray.filter(function (v, i) {
                    return seqIdArray.indexOf(v) === i;
                });
                let seqInd = 0;
                let keepGoingSeq = true;
                while (keepGoingSeq) {
                    const seqArray = sortOrderedEvents(
                        childrenSeq.filter((seqItem) => seqItem.eventTypeGroupSequenceIdentity === seqIdArray[seqInd])
                    );
                    if (seqArray && seqArray.length > 0) {
                        // Check if existing events contain dropdowns with items that are not in 'selItems'
                        let ind = 0;
                        let keepGoing = true;
                        while (keepGoing) {
                            const notSelectedDd = findNotSelectedDropdownItem(selItems, seqArray[ind]);
                            if (notSelectedDd) {
                                dropdownItem = notSelectedDd;
                                keepGoing = false;
                            } else {
                                ind++;
                                keepGoing = ind < seqArray.length;
                            }
                        }
                    }
                    if (dropdownItem) {
                        keepGoingSeq = false;
                    } else {
                        seqInd++;
                        keepGoingSeq = seqInd < seqIdArray.length;
                    }
                }
            }
        }
        return dropdownItem;
    };

    const findInvalidDropdown = (selItems: SelectOption[]): DropdownItemReg | null => {
        if (props.evTypeInput.eventTypeInputReg && props.evTypeInput.eventTypeInputReg.dropdownGroupReg && props.evType.parentSequenceNumber) {
            const invalidDd = findFirstPredessorInvalidDropdown(selItems);
            if (invalidDd) return invalidDd;
            return findFirstParentInvalidDropdown(selItems);
        }
        return null;
    };

    const styles = {
        control: (css) => ({ ...css, display: 'inline-flex', width: '100%', borderRadius: '0 4px 4px 0' }),
    };

    const getCargoInputs = (eventType: EventType, dropdownType: DropdownTypeEnum, includeDeleted: boolean): DropdownItem[] => {
        const dropdownItemArray: DropdownItem[] = [];

        if (eventType.levelId === RotationTreeEnum.PortCall) {
            const portCall: PortCall = getRequiredContextObj(selectedNode, RotationTreeEnum.PortCall) as PortCall;
            if (portCall) {
                const cargoOperationArray = portCall.berthCallArray.flatMap((bc) => bc.cargoOperationArray);
                if (cargoOperationArray && cargoOperationArray.length > 0) {
                    let itemOrder = 0;

                    cargoOperationArray.forEach((co) => {
                        if (filterCargoOperation(dropdownType, co, includeDeleted, false)) {
                            itemOrder++;
                            const newDd = dropdownItemFactory.newDropdownItemFromCode(co.cargoOperationId, -1, co.dropDownName, itemOrder, false);
                            dropdownItemArray.push(newDd);
                        }
                    });
                }
            }
        } else if (eventType.levelId === RotationTreeEnum.BerthCall) {
            const eventNode = selectedNode;
            const includeMoved = false;
            const berthCall: BerthCall = getRequiredContextObj(eventNode, RotationTreeEnum.BerthCall) as BerthCall;
            if (berthCall) {
                let itemOrder = 0;
                berthCall.cargoOperationArray.forEach((co) => {
                    if (filterCargoOperation(dropdownType, co, includeDeleted, includeMoved)) {
                        itemOrder++;
                        const newDd = dropdownItemFactory.newDropdownItemFromCode(co.cargoOperationId, -1, co.dropDownName, itemOrder, false);
                        dropdownItemArray.push(newDd);
                    }
                });
            }
        }
        return dropdownItemArray;
    };

    const getTankListInputs = (): DropdownItem[] => {
        let ind = 0;
        return vessel?.cargoTanks.map((tk) => dropdownItemFactory.newDropdownItemFromCode(tk.tankId, -1, tk.tankName, ind++, false));
    };

    const getBerthInputs = () => {
        let ind = 0;
        if (!selectedNode?.portCall?.port?.portId) return [];

        const portId = selectedNode.portCall.port.portId;
        return allBerths
            .filter((berth) => berth.portId === portId)
            .sort((a, b) => (a.berthLongName.toLowerCase() < b.berthLongName.toLowerCase() ? -1 : 1))
            .map((berth) => dropdownItemFactory.newDropdownItemFromCode(berth.berthId, -1, berth.berthLongName, ind++, false));
    };

    const isTankListDropdown = (drTypeId: DropdownTypeEnum): boolean => {
        return drTypeId === DropdownTypeEnum.Tank;
    };

    const inputHandler = (selection: any, isCreateable?: boolean | null) => {
        const errorMsg: ErrorMsg = new ErrorMsg(false, 'Dropdown valid', false, null);
        let selItems: SelectOption[] = [];

        if (isCreateable && selection.label === selection.value) {
            selection = new SelectOption('-1', selection.label);
        }

        if (eventTypeInput?.dropdownGroup?.multiple) {
            selItems = selection;
        } else {
            selItems.push(selection);
        }
        const ddItem = findInvalidDropdown(selItems);

        if (!ddItem) {
            errorMsg.error = !isDropdownInputValid(props.evTypeInput, selItems);
            if (errorMsg.error) {
                errorMsg.message = 'Dropdown seletion required';
            }
        } else {
            errorMsg.error = true;
            errorMsg.message = "Dropdown item '" + ddItem.dropdownItemValue + "' cannot be unselected";
        }
        props.onChange(selItems, eventTypeInput?.eventTypeInputId, !selection || selection.length === 0, errorMsg);
    };

    const getSelectedItems = () => {
        if (props.evTypeInput && props.evTypeInput.eventTypeInputReg && props.evTypeInput.eventTypeInputReg.dropdownGroupReg) {
            const selItemList = props.evTypeInput.eventTypeInputReg.dropdownGroupReg.dropdownItemRegList.map((item) => {
                return new SelectOption(item.dropdownItemId.toString(), item.dropdownItemValue);
            });
            return selItemList ?? [];
        }
        return [];
    };

    return (
        <>
            {eventTypeInput && (
                <InputWrapper>
                    <InputHeadingWrapper>{resolveHeading(eventTypeInput)}</InputHeadingWrapper>
                    <InputFieldWrapper>
                        <DropdownInputWrapper isNotValid={!isDropdownInputValid(props.evTypeInput, getSelectedItems())}>
                            {/* <DropdownInputWrapper isNotValid={!isValid}> */}
                            {eventTypeInput.dropdownGroup?.multiple && (
                                <MultiSelect
                                    value={getSelectedItems()}
                                    options={mappedOptions()}
                                    onChange={inputHandler}
                                    className="select-input"
                                    labelledBy="Select"
                                    disableSearch={true}
                                />
                            )}
                            {!eventTypeInput.dropdownGroup?.multiple && (
                                <>
                                    {isBerthDropdown(eventTypeInput.dropdownGroup.dropdownTypeId) && (
                                        <Creatable
                                            value={getSelectedItems()}
                                            options={mappedOptions()}
                                            onChange={(e) => inputHandler(e, true)}
                                            placeholder={'Select or type to create'}
                                            styles={styles}
                                            isSearchable={true}
                                            isMulti={false}
                                            className="select-input"
                                        />
                                    )}
                                    {!isBerthDropdown(eventTypeInput.dropdownGroup.dropdownTypeId) && (
                                        <Select
                                            value={getSelectedItems()}
                                            options={mappedOptions()}
                                            onChange={(e) => inputHandler(e)}
                                            styles={styles}
                                            isSearchable={false}
                                            isMulti={false}
                                            className="select-input"
                                        />
                                    )}
                                </>
                            )}
                        </DropdownInputWrapper>
                    </InputFieldWrapper>
                </InputWrapper>
            )}
        </>
    );
};

export default DropdownInputComponent;
